import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [importantDates, setImportantDates] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const [cats, ev, ts, rs, imp] = await Promise.all([
        api.get("/categories"),
        api.get("/events"),
        api.get("/tasks"),
        api.get("/routines"),
        api.get("/important-dates"),
      ]);
      setCategories(cats.data);
      setEvents(ev.data);
      setTasks(ts.data);
      setRoutines(rs.data);
      setImportantDates(imp.data);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const catById = (id) => categories.find((c) => c.id === id);

  // Events CRUD
  const createEvent = async (payload) => {
    const { data } = await api.post("/events", payload);
    setEvents((prev) => [...prev, data]);
    return data;
  };
  const updateEvent = async (id, patch) => {
    const { data } = await api.patch(`/events/${id}`, patch);
    setEvents((prev) => prev.map((e) => (e.id === id ? data : e)));
    return data;
  };
  const deleteEvent = async (id) => {
    const parentId = String(id).split("::")[0];
    const item = events.find((e) => e.id === parentId);
    await api.delete(`/events/${id}`);
    setEvents((prev) => prev.filter((e) => e.id !== parentId && !String(e.id).startsWith(parentId + "::")));
    return item;
  };
  const restoreEvent = async (item) => {
    if (!item) return;
    const { id, user_id, created_at, updated_at, recurring_parent_id, ...payload } = item;
    const { data } = await api.post("/events", payload);
    setEvents((prev) => [...prev, data]);
    return data;
  };
  const skipOccurrence = async (id, date) => {
    const parentId = String(id).split("::")[0];
    const { data } = await api.post(`/events/${parentId}/skip`, { date });
    setEvents((prev) => prev.map((e) => (e.id === parentId ? data : e)));
    return data;
  };
  const unskipOccurrence = async (id, date) => {
    const parentId = String(id).split("::")[0];
    const { data } = await api.post(`/events/${parentId}/unskip`, { date });
    setEvents((prev) => prev.map((e) => (e.id === parentId ? data : e)));
    return data;
  };

  // Tasks CRUD
  const createTask = async (payload) => {
    const { data } = await api.post("/tasks", payload);
    setTasks((prev) => [...prev, data]);
    return data;
  };
  const updateTask = async (id, patch) => {
    const { data } = await api.patch(`/tasks/${id}`, patch);
    setTasks((prev) => prev.map((t) => (t.id === id ? data : t)));
    return data;
  };
  const deleteTask = async (id) => {
    const item = tasks.find((t) => t.id === id);
    await api.delete(`/tasks/${id}`);
    setTasks((prev) => prev.filter((t) => t.id !== id));
    return item;
  };
  const restoreTask = async (item) => {
    if (!item) return;
    const { id, user_id, created_at, updated_at, ...payload } = item;
    const { data } = await api.post("/tasks", payload);
    setTasks((prev) => [...prev, data]);
    return data;
  };

  // Routines
  const createRoutine = async (payload) => {
    const { data } = await api.post("/routines", payload);
    setRoutines((prev) => [...prev, data]);
    return data;
  };
  const deleteRoutine = async (id) => {
    const item = routines.find((r) => r.id === id);
    await api.delete(`/routines/${id}`);
    setRoutines((prev) => prev.filter((r) => r.id !== id));
    return item;
  };
  const restoreRoutine = async (item) => {
    if (!item) return;
    const { id, user_id, created_at, ...payload } = item;
    const { data } = await api.post("/routines", payload);
    setRoutines((prev) => [...prev, data]);
    return data;
  };
  const applyRoutine = async (id, date, startTime) => {
    const { data } = await api.post(`/routines/${id}/apply`, { date, start_time: startTime });
    setEvents((prev) => [...prev, ...data.created]);
    return data.created;
  };

  // Important dates
  const createImportant = async (payload) => {
    const { data } = await api.post("/important-dates", payload);
    setImportantDates((prev) => [...prev, data]);
    return data;
  };
  const deleteImportant = async (id) => {
    const item = importantDates.find((i) => i.id === id);
    await api.delete(`/important-dates/${id}`);
    setImportantDates((prev) => prev.filter((i) => i.id !== id));
    return item;
  };
  const restoreImportant = async (item) => {
    if (!item) return;
    const { id, user_id, created_at, ...payload } = item;
    const { data } = await api.post("/important-dates", payload);
    setImportantDates((prev) => [...prev, data]);
    return data;
  };

  // Categories
  const createCategory = async (payload) => {
    const { data } = await api.post("/categories", payload);
    setCategories((prev) => [...prev, data]);
    return data;
  };
  const updateCategory = async (id, payload) => {
    const { data } = await api.patch(`/categories/${id}`, payload);
    setCategories((prev) => prev.map((c) => (c.id === id ? data : c)));
    return data;
  };
  const deleteCategory = async (id) => {
    const item = categories.find((c) => c.id === id);
    await api.delete(`/categories/${id}`);
    setCategories((prev) => prev.filter((c) => c.id !== id));
    return item;
  };
  const restoreCategory = async (item) => {
    if (!item) return;
    const { id, user_id, created_at, ...payload } = item;
    const { data } = await api.post("/categories", payload);
    setCategories((prev) => [...prev, data]);
    return data;
  };

  return (
    <DataContext.Provider
      value={useMemo(() => ({
        loading, categories, events, tasks, routines, importantDates,
        catById, refresh,
        createEvent, updateEvent, deleteEvent, restoreEvent, skipOccurrence, unskipOccurrence,
        createTask, updateTask, deleteTask, restoreTask,
        createRoutine, deleteRoutine, restoreRoutine, applyRoutine,
        createImportant, deleteImportant, restoreImportant,
        createCategory, updateCategory, deleteCategory, restoreCategory,
      }),
      // The callback functions close over the state fields listed below.
      // Recomputing the value only when state changes is intentional.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [loading, categories, events, tasks, routines, importantDates])}
    >
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
